import mixpanel, { Dict } from 'mixpanel-browser';
import { AnalyticsService } from '../types';
import { extractCurrentUrlUtmParams, addPrefixToObjectProperties } from 'src/shared/utils';

export class MixpanelService implements AnalyticsService {
  id = 'mixpanel';

  isDebugOnly = false;

  private token: string;

  constructor(token: string, isDebugOnly: boolean) {
    this.token = token;
    this.isDebugOnly = isDebugOnly;
  }

  private trackAndStoreUtmParameters() {
    try {
      const utmParams = extractCurrentUrlUtmParams();
      const cachedUtmParams = localStorage.getItem('utm_params');

      const actualUtmParams =
        Object.keys(utmParams).length > 0 ? utmParams : cachedUtmParams ? JSON.parse(cachedUtmParams) : {};

      const prefixedUtmParams = addPrefixToObjectProperties(actualUtmParams, 'initial_');

      if (Object.keys(utmParams).length > 0) {
        localStorage.setItem('utm_params', JSON.stringify(utmParams));
      }

      if (Object.keys(actualUtmParams).length > 0) {
        this.setSuperProperties(actualUtmParams);
        this.setSuperPropertiesOnce(prefixedUtmParams);

        this.setUserProperties(actualUtmParams);
        this.setUserPropertiesOnce(prefixedUtmParams);
      }
    } catch (e) {}
  }

  initialize() {
    try {
      !this.isDebugOnly &&
        mixpanel.init(this.token, {
          track_pageview: false,
          debug: import.meta.env.DEV,
        });
      console.log('[Mixpanel]: Initialized 🚀');

      this.trackAndStoreUtmParameters();
    } catch (e) {
      console.log(`[Mixpanel]: Initialized fail:`, e);
    }
  }

  sendEvent(eventName: string, properties?: Record<string, any>) {
    try {
      !this.isDebugOnly && mixpanel.track(eventName, properties);
      console.log(`[Mixpanel]: Track event: ${eventName}, with properties: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Track event ${eventName} error: `, e);
    }
  }

  identify(userId: string, properties?: Dict) {
    try {
      if (!this.isDebugOnly) {
        if (mixpanel.get_distinct_id() !== userId) {
          mixpanel.identify(userId);
        }
        properties && mixpanel.people.set(properties);
        this.trackAndStoreUtmParameters();
      }
      console.log(`[Mixpanel]: Identify user: ${userId}, with properties: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Identify user ${userId} error: `, e);
    }
  }

  reset() {
    try {
      !this.isDebugOnly && mixpanel.reset();
      console.log(`[Mixpanel]: Reset user`);
    } catch (e) {
      console.error(`[Mixpanel]: Reset user error: `, e);
    }
  }

  incrementUserProperty(property: string, value: number) {
    try {
      !this.isDebugOnly && mixpanel.people.increment(property, value);
      console.log(`[Mixpanel]: Increment user property: ${property}, width value: ${value}`);
    } catch (e) {
      console.error(`[Mixpanel]: Increment user property ${property} error: `, e);
    }
  }

  /**
   * Sets super properties that will be sent with every event
   *
   * @param properties - Object containing the super properties to set
   */
  setSuperProperties(properties: Record<string, any>) {
    try {
      if (Object.keys(properties).length === 0) {
        return;
      }

      !this.isDebugOnly && mixpanel.register(properties);
      console.log(`[Mixpanel]: Set super properties: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Set super properties error: `, e);
    }
  }

  /**
   * Sets super properties that will be sent with every event, but can be overridden by other setSuperProperties or setSuperPropertiesOnce calls
   *
   * @param properties - Object containing the super properties to set once
   */
  setSuperPropertiesOnce(properties: Record<string, any>) {
    try {
      if (Object.keys(properties).length === 0) {
        return;
      }

      !this.isDebugOnly && mixpanel.register_once(properties);
      console.log(`[Mixpanel]: Set super properties once: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Set super properties once error: `, e);
    }
  }

  /**
   * Sets user properties that will be updated or created
   *
   * @param properties - Object containing the user properties to set
   */
  setUserProperties(properties: Record<string, any>) {
    try {
      !this.isDebugOnly && mixpanel.people.set(properties);
      console.log(`[Mixpanel]: Set user properties: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Set user properties error: `, e);
    }
  }

  /**
   * Sets user properties that will only be set if they don't already exist
   *
   * @param properties - Object containing the user properties to set once
   */
  setUserPropertiesOnce(properties: Record<string, any>) {
    try {
      !this.isDebugOnly && mixpanel.people.set_once(properties);
      console.log(`[Mixpanel]: Set user properties once: `, properties);
    } catch (e) {
      console.error(`[Mixpanel]: Set user properties once error: `, e);
    }
  }
}
